import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Truck, CheckCircle, Info, ArrowSquareOut,
} from "@phosphor-icons/react";
import client from "../lib/api";

/**
 * MotiveConnectCard — mirrors SamsaraConnectCard but for Motive
 * (formerly KeepTruckin). Motive is the second-most-common ELD in
 * the small-fleet segment.
 */
export default function MotiveConnectCard() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState("");
  const [connecting, setConnecting] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await client.get("/motive/status");
      setStatus(data);
    } catch {
      setStatus(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const connect = async () => {
    const t = token.trim();
    if (!t) {
      toast.error("Paste your Motive API token first.");
      return;
    }
    setConnecting(true);
    try {
      const { data } = await client.post("/motive/connect", { api_token: t });
      toast.success(`Connected to ${data.org_name || "Motive"} ✓`);
      setToken("");
      await load();
    } catch (e) {
      toast.error(
        e?.response?.data?.detail
          || "Motive rejected the token. Verify it's active in the Motive dashboard.",
      );
    } finally {
      setConnecting(false);
    }
  };

  const disconnect = async () => {
    if (!confirm("Disconnect Motive?")) return;
    try {
      await client.delete("/motive/disconnect");
      toast.success("Disconnected from Motive.");
      await load();
    } catch {
      toast.error("Could not disconnect. Try again.");
    }
  };

  const syncDrivers = async () => {
    setSyncing(true);
    try {
      const { data } = await client.post("/motive/sync-drivers");
      toast.success(
        `Synced ${data.total} drivers · ${data.imported} new, ${data.updated} updated`,
      );
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || "Sync failed. Check your connection.",
      );
    } finally {
      setSyncing(false);
    }
  };

  if (loading) return null;
  const isConnected = status?.connected;

  return (
    <div data-testid="motive-connect-card" className="card-surface p-5">
      <div className="flex items-start gap-3 mb-4">
        <div className="shrink-0 w-10 h-10 rounded-lg bg-indigo-500/15 text-indigo-300 flex items-center justify-center">
          <Truck size={20} weight="fill" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="font-display text-lg font-semibold text-white">
              Motive ELD
            </div>
            {isConnected ? (
              <span
                data-testid="motive-connect-status-connected"
                className="inline-flex items-center gap-1 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 px-2 py-0.5 text-[10px] uppercase tracking-wide"
              >
                <CheckCircle size={10} weight="fill" /> Connected
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 rounded-full bg-neutral-500/15 text-neutral-400 border border-neutral-500/30 px-2 py-0.5 text-[10px] uppercase tracking-wide">
                Not connected
              </span>
            )}
          </div>
          <p className="text-sm text-neutral-400 mt-1">
            {isConnected ? (
              <>
                Connected to <b className="text-white">{status.org_name || "Motive"}</b>.
                HOS and GPS appear alongside your other ELD data on Dispatch.
              </>
            ) : (
              <>
                Pull driver HOS and live GPS from Motive (formerly KeepTruckin).
                Most common ELD for owner-operators and 1-50 truck fleets.
              </>
            )}
          </p>
        </div>
      </div>

      {isConnected ? (
        <div className="space-y-3">
          <div className="rounded-lg border border-white/10 bg-white/[0.02] p-3 text-xs text-neutral-400 flex items-start gap-2">
            <Info size={12} className="mt-0.5 shrink-0" />
            <div>
              Connected as <b className="text-white">{status.connected_by || "—"}</b>.
              Motive data merges with Samsara (if connected) on the Dispatch view.
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button" onClick={syncDrivers} disabled={syncing}
              data-testid="motive-sync-drivers-btn"
              className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-xs font-semibold bg-indigo-500 hover:bg-indigo-400 text-black disabled:opacity-50"
            >
              {syncing ? "Syncing…" : "Sync drivers from Motive"}
            </button>
            <button
              type="button" onClick={disconnect}
              data-testid="motive-disconnect-btn"
              className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-xs bg-white/[0.04] border border-white/10 text-neutral-300 hover:bg-white/[0.08]"
            >
              Disconnect
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          <label className="block">
            <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold mb-1">
              Motive API token
            </div>
            <input
              type="password" value={token}
              onChange={(e) => setToken(e.target.value)}
              placeholder="mot_api_..."
              data-testid="motive-connect-token-input"
              className="w-full rounded-lg bg-white/[0.03] border border-white/10 px-3 py-2 text-sm text-white font-mono focus:outline-none focus:border-indigo-400/50"
            />
          </label>
          <div className="text-xs text-neutral-500 flex items-start gap-1.5">
            <div>
              Generate one at{" "}
              <a
                href="https://developer.gomotive.com/authentication"
                target="_blank" rel="noopener noreferrer"
                className="text-indigo-400 hover:underline inline-flex items-center gap-1"
              >
                Motive Dashboard → API Keys
                <ArrowSquareOut size={10} />
              </a>. Stored encrypted per-account.
            </div>
          </div>
          <button
            type="button" onClick={connect}
            disabled={connecting || !token.trim()}
            data-testid="motive-connect-btn"
            className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-xs font-semibold bg-indigo-500 hover:bg-indigo-400 text-black disabled:opacity-50"
          >
            {connecting ? "Connecting…" : "Connect Motive"}
          </button>
        </div>
      )}
    </div>
  );
}
